import React, { useEffect, useState, useRef, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import StatusChip from "@/components/StatusChip";
import OrderTimeline from "@/components/OrderTimeline";
import ShareTrackingButton, { trackingUrl } from "@/components/ShareTrackingButton";
import { ArrowLeft, UploadSimple, CheckCircle, FileText, ImageSquare, Certificate } from "@phosphor-icons/react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { formatDate } from "@/lib/format";

const BYTES_PER_KB = 1024;

const STATUS_OPTIONS = [
  { key: "ORDER_RECEIVED", label: "Order Received" },
  { key: "RAW_MATERIALS", label: "Raw Materials" },
  { key: "IN_PRODUCTION", label: "In Production" },
  { key: "QUALITY_TESTING", label: "Quality Testing" },
  { key: "READY_FOR_SHIPMENT", label: "Ready for Shipment" },
  { key: "SHIPPED", label: "Shipped" },
  { key: "DELIVERED", label: "Delivered" },
];

export default function AdminOrderEdit() {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newStatus, setNewStatus] = useState("");
  const [notes, setNotes] = useState("");
  const [manufacturingDate, setManufacturingDate] = useState("");
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);
  const [uploadKind, setUploadKind] = useState("test_certificate");

  const fetchOrder = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/orders/${orderId}`);
      setOrder(res.data);
      setNewStatus(res.data.current_status);
      setManufacturingDate(res.data.manufacturing_date || "");
    } catch (e) {
      console.error("[AdminOrderEdit] fetchOrder failed:", e);
    }
    setLoading(false);
  }, [orderId]);

  useEffect(() => {
    fetchOrder();
  }, [fetchOrder]);

  const updateStatus = async () => {
    if (!newStatus) return;
    setSaving(true);
    try {
      await api.patch(`/orders/${orderId}/status`, {
        status_key: newStatus,
        notes,
        manufacturing_date: manufacturingDate || null,
      });
      toast.success("Order status updated");
      setNotes("");
      fetchOrder();
    } catch (e) {
      toast.error("Failed to update");
    }
    setSaving(false);
  };

  const uploadFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const form = new FormData();
    form.append("kind", uploadKind);
    form.append("file", file);
    try {
      await api.post(`/orders/${orderId}/files`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Uploaded ${file.name}`);
      fetchOrder();
      if (fileInputRef.current) fileInputRef.current.value = "";
    } catch (err) {
      toast.error("Upload failed");
    }
  };

  if (loading || !order) {
    return (
      <Layout>
        <div className="h-96 bg-white border border-zinc-200 animate-pulse" />
      </Layout>
    );
  }

  return (
    <Layout>
      <Link to="/admin" className="inline-flex items-center gap-2 mt-tiny text-zinc-500 hover:text-zinc-900 mb-6" data-testid="admin-back-link">
        <ArrowLeft size={14} /> Back to orders
      </Link>

      <div className="mb-6 flex flex-wrap items-center gap-3 bg-white border border-zinc-200 p-4">
        <div className="mt-tiny text-zinc-500 flex-1 min-w-[200px]">
          Public customer view · {trackingUrl(order.order_id)}
        </div>
        <a
          href={`/track/${order.order_id}`}
          target="_blank"
          rel="noreferrer"
          data-testid="admin-preview-tracking"
          className="inline-flex items-center gap-1.5 border border-zinc-200 hover:border-zinc-900 px-3 py-1.5 text-xs font-medium transition-colors"
        >
          Preview
        </a>
        <ShareTrackingButton orderId={order.order_id} orderNumber={order.order_number} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Left: order info + status control */}
        <div className="lg:col-span-7 space-y-6">
          <div className="bg-white border border-zinc-200 p-6 md:p-8">
            <div className="flex items-baseline justify-between flex-wrap gap-2">
              <div>
                <div className="mt-tiny text-zinc-400 font-mono-mt">{order.order_number}</div>
                <h1 className="font-display text-3xl font-black text-zinc-900 mt-1">{order.product_name}</h1>
                <div className="mt-2 text-zinc-500">{order.client_name} · {order.client_email}</div>
              </div>
              <StatusChip status={order.current_status} testId="admin-current-chip" />
            </div>

            <div className="mt-8 pt-6 border-t border-zinc-200">
              <div className="mt-tiny text-[#FF5A1F] mb-3">Update Production Status</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="mt-tiny text-zinc-500 mb-2 block">New Status</label>
                  <Select value={newStatus} onValueChange={setNewStatus}>
                    <SelectTrigger data-testid="status-update-select" className="h-11 border-zinc-200 rounded-none bg-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="rounded-none">
                      {STATUS_OPTIONS.map((s) => (
                        <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="mt-tiny text-zinc-500 mb-2 block">Manufacturing Date</label>
                  <Input
                    type="date"
                    data-testid="manufacturing-date-input"
                    value={manufacturingDate ? manufacturingDate.slice(0, 10) : ""}
                    onChange={(e) => setManufacturingDate(e.target.value)}
                    className="h-11 rounded-none border-zinc-200"
                  />
                </div>
              </div>
              <div className="mt-4">
                <label className="mt-tiny text-zinc-500 mb-2 block">Notes for this stage (optional)</label>
                <Textarea
                  data-testid="status-notes-input"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Add stage-specific notes (e.g. QC passed EN 12600 impact test)..."
                  className="rounded-none border-zinc-200 min-h-[90px]"
                />
              </div>
              <button
                onClick={updateStatus}
                disabled={saving}
                data-testid="update-status-button"
                className="mt-5 inline-flex items-center gap-2 bg-[#0047AB] hover:bg-[#003380] text-white px-6 py-3 font-medium transition-colors disabled:opacity-60"
              >
                <CheckCircle size={18} weight="fill" /> {saving ? "Saving..." : "Update Status & Notify Client"}
              </button>
            </div>
          </div>

          {/* Upload Section */}
          <div className="bg-white border border-zinc-200 p-6 md:p-8">
            <div className="mt-tiny text-[#FF5A1F] mb-3">Upload Documents</div>
            <div className="flex flex-col md:flex-row gap-3">
              <Select value={uploadKind} onValueChange={setUploadKind}>
                <SelectTrigger data-testid="upload-kind-select" className="h-11 border-zinc-200 rounded-none bg-white md:w-64">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="rounded-none">
                  <SelectItem value="test_certificate">Test Certificate (PDF)</SelectItem>
                  <SelectItem value="warranty">Warranty Document</SelectItem>
                  <SelectItem value="drawing">Drawing / Dimensions</SelectItem>
                  <SelectItem value="spec_sheet">Specifications Sheet</SelectItem>
                  <SelectItem value="tech_datasheet">Technical Data Sheet</SelectItem>
                  <SelectItem value="photo">Production Photo</SelectItem>
                </SelectContent>
              </Select>
              <label
                data-testid="upload-file-label"
                className="flex-1 border border-dashed border-zinc-300 hover:border-[#0047AB] hover:bg-zinc-50 p-4 flex items-center justify-center gap-3 cursor-pointer transition-colors"
              >
                <UploadSimple size={20} className="text-[#0047AB]" />
                <span className="text-sm font-medium text-zinc-700">Choose file to upload</span>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={uploadFile}
                  className="hidden"
                  data-testid="upload-file-input"
                  accept=".pdf,.jpg,.jpeg,.png,.webp"
                />
              </label>
            </div>

            <div className="mt-6 grid gap-2">
              {(order.files || []).length === 0 ? (
                <div className="text-sm text-zinc-400">No documents uploaded yet</div>
              ) : (
                (order.files || []).map((f) => (
                  <div key={f.file_id} className="flex items-center justify-between border border-zinc-200 p-3">
                    <div className="flex items-center gap-3 min-w-0">
                      {f.kind === "photo" ? <ImageSquare size={18} className="text-[#0047AB]" /> :
                       f.kind === "test_certificate" ? <Certificate size={18} className="text-[#0047AB]" /> :
                       <FileText size={18} className="text-[#0047AB]" />}
                      <div className="min-w-0">
                        <div className="text-sm text-zinc-900 truncate">{f.original_filename}</div>
                        <div className="mt-tiny text-zinc-400">{f.kind.replace("_", " ")} · {(f.size / BYTES_PER_KB).toFixed(1)} KB</div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Right: timeline */}
        <div className="lg:col-span-5">
          <div className="bg-white border border-zinc-200 p-6 md:p-8 lg:sticky lg:top-24">
            <div className="mt-tiny text-zinc-400 mb-6">Current Timeline</div>
            <OrderTimeline
              timeline={order.timeline || []}
              current_status={order.current_status}
              orderId={order.order_id}
              editable={true}
              onChanged={fetchOrder}
            />
            <div className="mt-6 pt-6 border-t border-zinc-200 mt-tiny text-zinc-500">
              Expected delivery: <span className="text-zinc-900 font-medium">{formatDate(order.expected_delivery)}</span>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
